import { createColumnHelper } from '@tanstack/react-table';

import { NamespaceSelect } from '@/react/portainer/policies/components/kubernetes/rbac/NamespaceSelect';
import { RegistrySelector } from '@/react/portainer/policies/components/RegistrySelector/RegistrySelector';

import { Link } from '@@/Link';
import {
  actionsColumn,
  isIdForNewRow,
} from '@@/datatables/editable/actionsColumn';
import { isEditableTableMeta } from '@@/datatables/editable/isEditableTableMeta';

import { AccessRow } from './types';

export function getColumns(
  accessRows: AccessRow[],
  onChange: (value: AccessRow[]) => void,
  onRemove: (item: AccessRow) => void,
  handleNamespacesChange: (item: AccessRow, namespaces: string[]) => void,
  availableNamespaces: string[]
) {
  const columnHelper = createColumnHelper<AccessRow>();

  return [
    columnHelper.accessor('registryName', {
      header: 'Registry',
      cell: ({ row: { original } }) => {
        if (isIdForNewRow(original.id)) {
          return (
            <RegistrySelector
              value={original.registryId}
              onChange={(val) => handleRegistryChange(original, val)}
              filter={(registries) => registries.filter((r) => r.Id !== 0)}
            />
          );
        }

        return (
          <Link
            to={`/admin/registries/${original.registryId}`}
            data-cy={`registry-link-${original.registryId}`}
          >
            {original.registryName || 'Unknown'}
          </Link>
        );
      },
      meta: {
        className: 'w-1/3',
      },
    }),
    columnHelper.accessor('namespaces', {
      header: 'Namespaces',
      id: 'namespaces',
      cell: ({ row: { original, index }, table }) => {
        if (!isEditableTableMeta(table.options.meta)) {
          return null;
        }

        const editableRowIndex = table.options.meta.getEditableRow();
        return index === editableRowIndex || isIdForNewRow(original.id) ? (
          <NamespacesCell
            item={original}
            onChange={handleNamespacesChange}
            availableNamespaces={availableNamespaces}
          />
        ) : (
          <div>{`${
            original.namespaces ? original.namespaces.join(', ') : '-'
          }`}</div>
        );
      },
      meta: { width: '40%', className: 'overflow-hidden' },
    }),
    actionsColumn<AccessRow>(onRemove, (row: AccessRow) =>
      isIdForNewRow(row.id)
    ),
  ];

  function handleRegistryChange(item: AccessRow, registryId: number | null) {
    updateAccess(item, (prev) => ({
      ...prev,
      registryId,
    }));
  }

  function updateAccess(
    item: AccessRow,
    updater: (prev: AccessRow) => AccessRow
  ) {
    const newAccessRow = [...accessRows];
    const index = newAccessRow.findIndex(
      (access) => access.registryId === item.registryId
    );
    if (index !== -1) {
      newAccessRow[index] = updater(newAccessRow[index]);
      onChange(newAccessRow);
    }
  }
}

function NamespacesCell({
  item,
  onChange,
  availableNamespaces,
}: {
  item: AccessRow;
  onChange: (item: AccessRow, namespaces: string[]) => void;
  availableNamespaces: string[];
}) {
  return (
    <NamespaceSelect
      selectedNamespaces={item.namespaces || []}
      onChange={(namespaces) => onChange(item, namespaces)}
      placeholder="e.g. default, production"
      data-cy={`namespaces-input-${item.registryId}`}
      namespaces={availableNamespaces}
    />
  );
}
